import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    Box,
    CircularProgress
} from '@mui/material';

function DeductionsContributionsForm({ open, onClose, salarySpecificationId, onSaved }) {
    const { register, handleSubmit, formState: { errors }, reset } = useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingData, setLoadingData] = useState(false);
    const [existingId, setExistingId] = useState(null);

    // Load existing deductions contributions
    const { data: existingData, isLoading: isLoadingExisting } = useQuery(
        ['deductionsContributions', salarySpecificationId],
        () => {
            if (!salarySpecificationId) return null;
            return JobHttpService.loadDeductionsContributions({ salary_specification: salarySpecificationId });
        },
        {
            enabled: !!salarySpecificationId && open,
            onSuccess: (data) => {
                // Handle different response formats
                let deduction = null;
                if (data) {
                    if (data.results && data.results.length > 0) {
                        deduction = data.results[0];
                    } else if (Array.isArray(data) && data.length > 0) {
                        deduction = data[0];
                    } else if (data.id) {
                        // Single object response
                        deduction = data;
                    }
                }

                if (deduction) {
                    setExistingId(deduction.id);
                    reset({
                        paid_vacation_days: deduction.paid_vacation_days || '',
                        accumulated_gross: deduction.accumulated_gross || '',
                        preliminary_tax: deduction.preliminary_tax || '',
                        taxable_salary: deduction.taxable_salary || '',
                        gross_salary: deduction.gross_salary || '',
                        employer_contribution_percentage: deduction.employer_contribution_percentage || '',
                        accumulated_benefits: deduction.accumulated_benefits || '',
                        vacation_pay: deduction.vacation_pay || '',
                        vacation_pay_salary: deduction.vacation_pay_salary || '',
                        worked_time: deduction.worked_time || '',
                        overtime: deduction.overtime || '',
                        absence: deduction.absence || '',
                        flex_time: deduction.flex_time || '',
                        net_salary: deduction.net_salary || ''
                    });
                } else {
                    setExistingId(null);
                    reset({
                        paid_vacation_days: '',
                        accumulated_gross: '',
                        preliminary_tax: '',
                        taxable_salary: '',
                        gross_salary: '',
                        employer_contribution_percentage: '',
                        accumulated_benefits: '',
                        vacation_pay: '',
                        vacation_pay_salary: '',
                        worked_time: '',
                        overtime: '',
                        absence: '',
                        flex_time: '',
                        net_salary: ''
                    });
                }
            }
        }
    );

    useEffect(() => {
        if (open && !isLoadingExisting) {
            // Data will be loaded via query
        }
    }, [open, isLoadingExisting]);

    const onSubmit = async (data) => {
        setLoading(true);
        setError('');

        try {
            const submitData = {
                salary_specification: salarySpecificationId,
                paid_vacation_days: data.paid_vacation_days ? parseInt(data.paid_vacation_days) : null,
                accumulated_gross: data.accumulated_gross ? parseFloat(data.accumulated_gross) : null,
                preliminary_tax: data.preliminary_tax ? parseFloat(data.preliminary_tax) : null,
                taxable_salary: data.taxable_salary ? parseFloat(data.taxable_salary) : null,
                gross_salary: data.gross_salary ? parseFloat(data.gross_salary) : null,
                employer_contribution_percentage: data.employer_contribution_percentage ? parseFloat(data.employer_contribution_percentage) : null,
                accumulated_benefits: data.accumulated_benefits ? parseFloat(data.accumulated_benefits) : null,
                vacation_pay: data.vacation_pay ? parseFloat(data.vacation_pay) : null,
                vacation_pay_salary: data.vacation_pay_salary ? parseFloat(data.vacation_pay_salary) : null,
                worked_time: data.worked_time ? parseFloat(data.worked_time) : null,
                overtime: data.overtime ? parseFloat(data.overtime) : null,
                absence: data.absence ? parseFloat(data.absence) : null,
                flex_time: data.flex_time ? parseFloat(data.flex_time) : null,
                net_salary: data.net_salary ? parseFloat(data.net_salary) : null
            };

            let response;
            if (existingId) {
                response = await JobHttpService.updateDeductionContribution(existingId, submitData);
            } else {
                response = await JobHttpService.createDeductionContribution(submitData);
            }

            if (response && !response.detail && !response.error) {
                if (onSaved) {
                    onSaved();
                }
                onClose();
            } else {
                setError(response?.detail || response?.error || 'Failed to save deductions contributions');
            }
        } catch (err) {
            console.error('Error saving deductions contributions:', err);
            setError(err?.message || 'Failed to save deductions contributions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    Deduction Contributions
                </DialogTitle>
                <DialogContent>
                    {isLoadingExisting && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {!isLoadingExisting && (
                        <>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Paid Vacation Days"
                                        type="number"
                                        {...register("paid_vacation_days")}
                                        error={Boolean(errors.paid_vacation_days)}
                                        helperText={errors.paid_vacation_days?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Accumulated Gross"
                                        type="number"
                                        step="0.01"
                                        {...register("accumulated_gross")}
                                        error={Boolean(errors.accumulated_gross)}
                                        helperText={errors.accumulated_gross?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Preliminary Tax"
                                        type="number"
                                        step="0.01"
                                        {...register("preliminary_tax")}
                                        error={Boolean(errors.preliminary_tax)}
                                        helperText={errors.preliminary_tax?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Taxable Salary"
                                        type="number"
                                        step="0.01"
                                        {...register("taxable_salary")}
                                        error={Boolean(errors.taxable_salary)}
                                        helperText={errors.taxable_salary?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Gross Salary"
                                        type="number"
                                        step="0.01"
                                        {...register("gross_salary")}
                                        error={Boolean(errors.gross_salary)}
                                        helperText={errors.gross_salary?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Employer Contribution Percentage"
                                        type="number"
                                        step="0.01"
                                        {...register("employer_contribution_percentage")}
                                        error={Boolean(errors.employer_contribution_percentage)}
                                        helperText={errors.employer_contribution_percentage?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Accumulated Benefits"
                                        type="number"
                                        step="0.01"
                                        {...register("accumulated_benefits")}
                                        error={Boolean(errors.accumulated_benefits)}
                                        helperText={errors.accumulated_benefits?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Vacation Pay"
                                        type="number"
                                        step="0.01"
                                        {...register("vacation_pay")}
                                        error={Boolean(errors.vacation_pay)}
                                        helperText={errors.vacation_pay?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Vacation Pay Salary"
                                        type="number"
                                        step="0.01"
                                        {...register("vacation_pay_salary")}
                                        error={Boolean(errors.vacation_pay_salary)}
                                        helperText={errors.vacation_pay_salary?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Worked Time"
                                        type="number"
                                        step="0.01"
                                        {...register("worked_time")}
                                        error={Boolean(errors.worked_time)}
                                        helperText={errors.worked_time?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Overtime"
                                        type="number"
                                        step="0.01"
                                        {...register("overtime")}
                                        error={Boolean(errors.overtime)}
                                        helperText={errors.overtime?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Absence"
                                        type="number"
                                        step="0.01"
                                        {...register("absence")}
                                        error={Boolean(errors.absence)}
                                        helperText={errors.absence?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Flex Time"
                                        type="number"
                                        step="0.01"
                                        {...register("flex_time")}
                                        error={Boolean(errors.flex_time)}
                                        helperText={errors.flex_time?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Net Salary"
                                        type="number"
                                        step="0.01"
                                        {...register("net_salary")}
                                        error={Boolean(errors.net_salary)}
                                        helperText={errors.net_salary?.message}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading || isLoadingExisting}
                    >
                        {loading ? <CircularProgress size={20} /> : (existingId ? 'Update' : 'Save')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default DeductionsContributionsForm;
